/**
 * ENG-6195: shared contract for the admin debug surface — the diagnostic
 * projection types, the explicit DB column allow-lists, and the scope value
 * that authorises a read.
 *
 * Design council (ENG-6195) hard requirements baked in here:
 *
 *  1. **Diagnostic projection, never raw rows.** The debug surface returns
 *     verdicts / enums / safe metadata, NEVER credentials, tokens, transcripts,
 *     `agent_memories`, settings blobs, or PII. The column allow-lists below are
 *     the structural gate: the API selects exactly these columns (never
 *     `select('*')`), so a newly-added sensitive column on `agents` /
 *     `agent_integrations` is invisible to a cross-org reader until someone
 *     deliberately adds it here — the safe default for a cross-org egress tool.
 *
 *  2. **Scope is a value, not a hard-coded branch.** `DebugScope` is the
 *     parameter the whole surface pivots on. Staff resolve to `{ kind:
 *     'all-orgs' }`; the future org-admin case is a new branch returning
 *     `{ kind: 'single-org', orgId }` — every read takes a `DebugScope`, so the
 *     extension is a parameter, not a fork (ENG-6195 Architect).
 *
 * This module is pure types + constants + pure verdict helpers — no DB client,
 * no node:crypto — so it is safe to import from both the API and the
 * `@integrity-labs/augmented-admin-mcp` package.
 */

// ───────────────────────────── scope ─────────────────────────────

/**
 * The read-scope a debug caller is authorised for. Returned by the API's
 * `resolveDebugScope(caller, requestContext)`.
 *
 * - `all-orgs`   — Integrity Labs staff (owning org `is_internal = true`). No
 *                  organization filter is applied; the widening is a single
 *                  auditable line in the API.
 * - `single-org` — reserved for the future org-admin extension (ENG-6197 /
 *                  ENG-6198): the caller may read only `orgId`. Not produced by
 *                  any Slice-1 resolver branch yet, but every read already
 *                  honours it so adding the branch is wiring, not a rewrite.
 */
export type DebugScope =
  | { kind: 'all-orgs' }
  | { kind: 'single-org'; orgId: string };

/** True when `scope` permits reading rows belonging to `organizationId`. */
export function scopeAllowsOrg(scope: DebugScope, organizationId: string | null): boolean {
  if (scope.kind === 'all-orgs') return true;
  return organizationId != null && organizationId === scope.orgId;
}

// ───────────────────────── liveness verdicts ─────────────────────────

export type LivenessVerdict = 'fresh' | 'stale' | 'down' | 'unknown';

/**
 * Collapse a heartbeat/last-seen age into a coarse verdict. Pure, so the
 * projection never has to ship a raw timestamp a caller could correlate — the
 * enum is the diagnostic signal.
 *
 * Thresholds are deliberately generous (a managed agent heartbeats well inside
 * 2 min; 10 min without one is "down"). `null` age → `unknown` (never seen).
 */
export function livenessVerdict(ageSeconds: number | null): LivenessVerdict {
  if (ageSeconds == null) return 'unknown';
  if (ageSeconds < 0) return 'unknown';
  if (ageSeconds <= 120) return 'fresh';
  if (ageSeconds <= 600) return 'stale';
  return 'down';
}

/** Seconds between `iso` and `now` (default Date.now), or null if `iso` is null/invalid. */
export function ageSeconds(iso: string | null | undefined, nowMs: number = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((nowMs - t) / 1000);
}

// ───────────────────────── column allow-lists ─────────────────────────
//
// The EXACT base-table columns each read may select. Sensitive columns are
// deliberately absent: agents has no secret columns but we still enumerate;
// hosts omits `anthropic_api_key_fingerprint` and api-key internals;
// agent_integrations omits `credentials` / `config`; alerts omits nothing
// secret (payload can carry context, so it is NOT selected). NEVER `select('*')`.

export const AGENT_DEBUG_COLUMNS = [
  'agent_id',
  'team_id',
  'code_name',
  'display_name',
  'status',
  'environment',
  'risk_tier',
  'created_at',
  'updated_at',
  'last_heartbeat_at',
] as const;

export const HOST_DEBUG_COLUMNS = [
  'id',
  'name',
  'organization_id',
  'status',
  'framework',
  'framework_version',
  'last_seen_at',
  'ec2_instance_id',
  'ec2_region',
  'ec2_provisioning_status',
  'claude_auth_mode',
  'claude_auth_status',
  'claude_auth_expires_at',
  // ENG-8341: the agt CLI version the host reported, plus the pin it should
  // match. Added here as well as on the admin-debug RPCs so the support read
  // surface answers the same question - "is this host running current code?" -
  // rather than only the staff one.
  'agt_version',
  'desired_agt_cli_version',
] as const;

export const INTEGRATION_DEBUG_COLUMNS = [
  'id',
  'agent_id',
  'team_id',
  'definition_id',
  'status',
  'status_message',
  'auth_type',
  'last_connectivity_check_at',
  'last_connectivity_status',
  'consecutive_connectivity_failures',
  // ENG-9810: WHY the last probe reached its verdict. Without it a reader
  // cannot tell a measured failure from a probe that never ran — both render
  // as `down` with a climbing streak, and the streak is the only number an
  // open `integration_down` alert quotes. Measured 2026-09-01: two agents on
  // agt-aws-1 carrying `down`/58 whose live probe answers `unverified` ("gh not
  // found on PATH — the probe could not run, so this is NOT a verdict"). The
  // cause column already holds the discriminator (ENG-9173); it was simply not
  // on any operator-reachable surface.
  'last_connectivity_cause',
  // The companion counter (ENG-9122 AC6): consecutive cycles in which we failed
  // to MEASURE at all. `0` next to a live failure streak means the probe is
  // reaching the provider; a climbing value means it is blind, which is the
  // fact that distinguishes "this integration is broken" from "we stopped being
  // able to look at it".
  'consecutive_connectivity_unverified_cycles',
  'updated_at',
] as const;

export const ALERT_DEBUG_COLUMNS = [
  'id',
  'kind',
  'severity',
  'message',
  'team_id',
  'host_id',
  'agent_id',
  'source',
  'opened_at',
  'closed_at',
  'closed_reason',
  'acknowledged_at',
  'snoozed_until',
  // ENG-10030 — the two fields that make an alert row self-explaining.
  //
  // `dedupe_key` is the alert's IDENTITY, `<kind>:<subject id>`. Without it you
  // cannot tell which row an alert is about, only which agent — so a frozen
  // alert naming a deleted integration is indistinguishable from one naming a
  // live one. That is still an open inference on ENG-9956 for exactly this
  // reason.
  //
  // `updated_at` tells you whether `message` is CURRENT. `open_alert`
  // overwrites `message` on every re-raise and never touches `opened_at`, so a
  // message is a snapshot unless the monitor re-raised that
  // `(kind, dedupe_key)` since it opened — and it looks like live output either
  // way. Read the asymmetry on {@link AlertDebugProjection.updated_at} before
  // using it; it is conclusive in one direction only.
  'updated_at',
  'dedupe_key',
] as const;

// ───────────────────────── projection DTOs ─────────────────────────

export interface AgentDebugProjection {
  agent_id: string;
  code_name: string;
  display_name: string | null;
  status: string | null;
  environment: string | null;
  risk_tier: string | null;
  team_id: string | null;
  organization_id: string | null;
  organization_slug: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Coarse verdict from `last_heartbeat_at`; raw timestamp intentionally not shipped. */
  heartbeat_verdict: LivenessVerdict;
  heartbeat_age_seconds: number | null;
}

export interface HostDebugProjection {
  id: string;
  name: string | null;
  organization_id: string | null;
  status: string | null;
  framework: string | null;
  /** The Claude Code / framework version. NOT the agt CLI version - see below. */
  framework_version: string | null;
  /**
   * ENG-8341: the agt CLI version the host reported on its last heartbeat
   * (`hosts.agt_version`). Distinct from `framework_version`, and the one this
   * surface was missing: until now the fleet tools could report every host's
   * Claude Code version and nothing about the version of OUR code it runs, so a
   * host frozen 19 releases behind read as perfectly healthy.
   */
  agt_version: string | null;
  /**
   * ENG-8341: the operator pin from `hosts.desired_agt_cli_version`, NULL when
   * unpinned (follow the release channel). Carried alongside `agt_version`
   * because drift is only meaningful against the pin - a deliberately pinned
   * host sitting behind `latest` is CORRECT, and reading the installed version
   * without its target invites exactly that false positive.
   */
  desired_agt_cli_version: string | null;
  last_seen_verdict: LivenessVerdict;
  last_seen_age_seconds: number | null;
  ec2_instance_id: string | null;
  ec2_region: string | null;
  ec2_provisioning_status: string | null;
  claude_auth_mode: string | null;
  claude_auth_status: string | null;
  claude_auth_expires_at: string | null;
  /**
   * ENG-9664: why this host is NOT expected to be alive, or null when it is.
   *
   * WHY THE READ SURFACES NEED THIS. ENG-8390 established that "decommissioned"
   * and "has no instance" are different facts, and that the thing monitors want
   * is the UNION of them — `hosts.status = 'decommissioned'` OR
   * `ec2_provisioning_status` in (terminating, terminated). Crucially it did
   * NOT collapse that union into `status`, deliberately: a terminated host
   * stays `active` precisely so it can be re-provisioned.
   *
   * The monitors got that predicate. These projections did not. So a reader
   * filtering on `status = 'active'` — which is what the fleet tools do — sees
   * a terminated machine as a live one, and gets a wrong answer SILENTLY.
   * Measured: `lkg-1` read as "the last host still pinned to agt 0.28.610",
   * making a version cohort look one-worse than it was and sending someone to
   * unpin a machine that no longer exists. Three hosts have sat in this state
   * for over a month.
   *
   * Additive and always present, same contract as `agt_version` above: the key
   * is on the wire even when null, so "this host is alive" is distinguishable
   * from "this tool does not report teardown".
   *
   * The value mirrors the api-side `TornDownReason` union, which is the single
   * authoritative definition; a compile-time guard in the mapper fails the
   * build if the two ever drift.
   */
  torn_down_reason: 'decommissioned' | 'terminating' | 'terminated' | null;
}

export interface IntegrationDebugProjection {
  id: string;
  definition_id: string | null;
  status: string | null;
  status_message: string | null;
  auth_type: string | null;
  last_connectivity_check_at: string | null;
  last_connectivity_status: string | null;
  consecutive_connectivity_failures: number | null;
  /**
   * ENG-9810 / ENG-9173 — the structured cause behind the last verdict, so a
   * reader can tell a MEASURED failure from a probe that never ran. `not_run`
   * means the streak beside it is not evidence about the integration.
   */
  last_connectivity_cause: string | null;
  /** ENG-9122 AC6 — consecutive cycles in which the probe measured nothing. */
  consecutive_connectivity_unverified_cycles: number | null;
  updated_at: string | null;
}

// ─────────────── effective integration set (ENG-8271) ───────────────
//
// `IntegrationDebugProjection` above is a PER-AGENT row: it reads
// `agent_integrations` only, so an install made at team or org scope is absent
// from it. That is a silent partial read — the natural inference from "X is not
// in the agent's integration list" is "X is not installed", and that inference
// is wrong for every inherited install. It cost two rounds of misdiagnosis on
// 2026-07-30 (don was reported as missing Augmented Live; it is installed at
// Integrity Labs org scope).
//
// The types below are the honest read: the agent's EFFECTIVE set — the same
// three-scope resolution the host performs in `POST /host/agent-integrations`
// — with the scope each entry is inherited from attributed explicitly, so a
// reader can tell WHY the count is what it is instead of having to trust it.

/**
 * Which scope an effective-set entry is installed at. Mirrors
 * `integrations_view.scope` ('organization' is normalized to 'org' so the
 * three values are the same width in a diagnostic table).
 */
export type EffectiveIntegrationScope = 'agent' | 'team' | 'org';

/** The host-reported session-tool-bind verdict — the "is it actually wired?" signal. */
export type SessionToolBindVerdict =
  | 'bound'
  | 'missing'
  | 'unreachable'
  | 'unknown'
  /**
   * ENG-9114: the integration contributes no `.mcp.json` server (CLI-delivered
   * or builtin), so a bind verdict is not a question about it. This is the
   * answer that used to be a `null` - and `null` is what readers spent six days
   * misreading as a bind failure on ENG-8751.
   */
  | 'not_applicable';

/**
 * One entry of an agent's effective integration set, with scope attribution.
 *
 * `wired_verdict` is the closest available answer to "is this in the agent's
 * `.mcp.json` right now?" — it is the host's own last session-tool-bind probe
 * result (ENG-7220/ENG-7429), persisted centrally. It is deliberately NOT a
 * live `.mcp.json` read: the admin surface cannot read host files (they hold
 * credentials), so this is a recency-bounded host report. `null` means the host
 * has never probed this install, which is NOT the same as "not wired".
 */
export interface EffectiveIntegrationProjection {
  /** The install row id (in `organization_integrations` / `team_integrations` / `agent_integrations`). */
  id: string;
  definition_id: string | null;
  display_name: string | null;
  /** Which connection of the toolkit this is (ENG-7543); 'default' for the single-connection fleet. */
  connection_key: string;
  /** The scope this install lives at — the field whose absence caused ENG-8271. */
  scope: EffectiveIntegrationScope;
  /**
   * The agent / team / org row the install comes from: the agent_id for
   * `agent` scope, the team_id for `team`, the organization_id for `org`. Lets
   * a reader navigate to the console surface that owns the row.
   */
  source_id: string | null;
  status: string | null;
  status_message: string | null;
  auth_type: string | null;
  last_connectivity_check_at: string | null;
  last_connectivity_status: string | null;
  consecutive_connectivity_failures: number | null;
  /**
   * ENG-9810 / ENG-9173 — the structured cause behind the last verdict.
   *
   * Read it BEFORE acting on `consecutive_connectivity_failures`: a `not_run`
   * cause means the probe never reached the provider, so the streak beside it
   * measures the prober rather than the integration. The two are
   * indistinguishable without this field, which is how two agents sat at
   * `down`/58 on 2026-09-01 while a live probe of the same rows answered
   * `unverified`.
   */
  last_connectivity_cause: string | null;
  /**
   * ENG-9122 AC6 — consecutive cycles in which the probe measured NOTHING.
   * A non-zero value here is the tell that a verdict has gone blind rather
   * than bad.
   */
  consecutive_connectivity_unverified_cycles: number | null;
  /** Host's last session-tool-bind verdict; null ⇒ never probed (NOT "not wired"). */
  wired_verdict: SessionToolBindVerdict | null;
  last_session_tool_bind_at: string | null;
  /**
   * ENG-10197 — when the install row was created.
   *
   * Carried so two rows for the SAME `definition_id` on one agent can be told
   * apart by age. That question is not academic: the observed shape is a
   * healthy row alongside a long-dead one, and which of "a reconnect created a
   * second row instead of updating the first" or "the older row was always
   * there" is true decides whether the dead row is a reap or a fix.
   */
  created_at: string | null;
  updated_at: string | null;
}

/**
 * ENG-10197 — why a row that EXISTS for this agent is not in `integrations`.
 *
 * One value per predicate in `resolveEffectiveIntegrations`, so an excluded row
 * carries the name of the specific rule that removed it rather than a bare
 * "not in the list".
 */
export type IntegrationExclusionReason =
  /** ENG-7742: a control-plane-only connection, never provisioned to an agent. */
  | 'control_plane_only'
  /** ENG-9285: `status` outside the provisioned set (`active` | `configured`). */
  | 'unhealthy_status'
  /** A nearer scope displaced this row (agent > team > org). */
  | 'shadowed_by_more_specific'
  /** Two rows at the SAME scope shared `(definition_id, connection_key)`. */
  | 'duplicate_merge_key';

/**
 * ENG-10197 — one row that exists for the agent and is NOT in the effective set,
 * named in full rather than counted.
 *
 * ## The defect this closes
 *
 * ENG-9285 made the status filter assert that it acted (`excluded_unhealthy`)
 * and ENG-9285's follow-up named the definitions behind it. Neither gave the
 * ROW. So a genuinely broken integration could be established to exist and
 * still not be addressable: on `ea` (2026-09-14) an `integration_down` alert
 * sat at 322 consecutive failures against a `composio/outlook` row while the
 * agent's visible `composio/outlook` row read active / ok / 0 failures, and the
 * only surface anywhere that exposed the broken row's id was the ALERT's
 * `dedupe_key`. An integration with no alert — `sophie`, same day, same shape —
 * had no such surface at all, so its dead row was not merely hard to find, it
 * was unaddressable.
 *
 * That is what made ENG-10197 readable as "an alert against an integration that
 * does not exist". The integration existed. It was unlistable, which is a
 * different thing and demands the opposite action: an absent row is an orphan
 * to reap, a present-but-unlistable one is a zombie somebody still has to fix.
 *
 * ## Why all four reasons and not just the unhealthy one
 *
 * Every bucket had the same hole; only one of them had been noticed. Counting a
 * removal proves something is hidden, naming the definition proves WHICH
 * integration is hidden, and only the row proves WHICH INSTALL — which is the
 * question every repair, reap or escalation actually turns on. Restricting this
 * to `unhealthy_status` would leave `excluded_control_plane_only` as the one
 * remaining place a row can be known to exist and still not be named; that gap
 * is not hypothetical either, it is why "is `github` installed on maven?" could
 * not be answered `no` without a caveat.
 */
export interface ExcludedIntegrationProjection extends EffectiveIntegrationProjection {
  /** Which predicate removed this row. */
  exclusion_reason: IntegrationExclusionReason;
  /**
   * For `shadowed_by_more_specific`, the scope of the row that displaced this
   * one; `null` for every other reason. Makes a shadowing event readable in one
   * line ("the org row lost to an agent row") instead of requiring the reader to
   * cross-reference `integrations` by merge key.
   */
  displaced_by_scope: EffectiveIntegrationScope | null;
}

/**
 * Why a scope was NOT queried when resolving the effective set.
 *
 * This exists because of the failure mode ENG-8271 IS: a resolution that
 * returns fewer rows than the truth, for a reason indistinguishable from the
 * bug. If an agent has no `team_id`, the team-scope query is skipped and the
 * result is agent-only rows — byte-identical to the old partial read. Recording
 * the skip makes the two cases distinguishable, so this read can never become
 * the next silent under-report.
 */
export interface SkippedIntegrationScope {
  scope: EffectiveIntegrationScope;
  /** Machine-readable cause: the agent carries no team / the team carries no org. */
  reason: 'no_team_id' | 'no_organization_id';
}

/**
 * The result of resolving an agent's effective integration set.
 *
 * The counters are not decoration — each one is an ASSERTION that a filter or
 * precedence rule acted, so a surprising `effective_count` can be attributed
 * rather than guessed at:
 *
 *  - `by_scope`                  — where the count comes from. `org > 0` is the
 *                                  signal the old per-agent read was hiding rows.
 *  - `inherited_definition_ids`  — exactly the definitions a per-agent read misses.
 *  - `excluded_control_plane_only` — rows dropped by the ENG-7742 filter (a
 *                                  control-plane connection is never provisioned
 *                                  to an agent, so it must not be counted).
 *  - `shadowed_by_more_specific` — rows dropped because a nearer scope overrode
 *                                  them (agent > team > org).
 *  - `scopes_queried` / `scopes_skipped` — which scopes contributed at all.
 *
 * `effective_count` is defined to equal what `POST /host/agent-integrations`
 * returns for the same agent, which is what the manager logs as
 * `Integrations provisioned for '<code_name>' (N)`. That equality is the whole
 * point: it makes the number reconcilable against an independent source
 * instead of self-consistent.
 */
export interface EffectiveIntegrationSet {
  /** Reconciles with manager.log's `Integrations provisioned for '<agent>' (N)`. */
  effective_count: number;
  by_scope: Record<EffectiveIntegrationScope, number>;
  /** definition_ids reachable ONLY via team/org scope — invisible to a per-agent read. */
  inherited_definition_ids: string[];
  /** Rows dropped by the control-plane-only filter (ENG-7742). Asserts the filter acted. */
  excluded_control_plane_only: number;
  /**
   * ENG-9285: rows that EXIST for this agent but were dropped because their
   * `status` is outside the provisioned set (`active` | `configured`) — a
   * quarantined, errored or otherwise unhealthy install.
   *
   * This is the counter whose absence made a whole class of triage unreliable.
   * Every other filter here already asserted it acted; this one did not, so
   * `effective_count` was silently a HEALTHY-count, and "absent from the
   * effective set" was indistinguishable from "the row does not exist".
   *
   * That mattered because the two readings demand opposite actions. A row that
   * is gone is an ORPHAN whose climbing failure counter is pure noise and
   * should be reaped. A row that exists and is unhealthy is a ZOMBIE — a
   * genuinely broken integration someone still has to fix — and reaping it
   * would turn a customer's dead credential quiet instead of loud. Before this
   * counter, no tool on the admin surface could tell them apart, so a forced
   * probe returning `not_installed` (which derives from the same status filter
   * on `POST /host/agent-integrations`) was routinely read as proof of the
   * first when it was equally consistent with the second.
   */
  excluded_unhealthy: number;
  /**
   * The `definition_id`s behind {@link excluded_unhealthy}, sorted and deduped.
   *
   * The count alone answers "is anything hidden"; this answers "is the thing I
   * am looking at hidden", which is the question an operator actually has when
   * a probe says `not_installed`. Without it the counter proves a zombie exists
   * somewhere but not that THIS integration is one.
   */
  excluded_unhealthy_definition_ids: string[];
  /**
   * Rows dropped because a NEARER scope displaced a broader one (agent > team >
   * org). Asserts precedence acted — so it deliberately excludes same-scope
   * collisions, which are not precedence events.
   */
  shadowed_by_more_specific: number;
  /**
   * Rows dropped because two installs at the SAME scope shared
   * `(definition_id, connection_key)`. Base-table uniqueness should make this 0;
   * a non-zero value is an anomaly worth chasing, which is why it is counted
   * apart from `shadowed_by_more_specific` rather than inflating it.
   */
  duplicate_merge_keys: number;
  scopes_queried: EffectiveIntegrationScope[];
  scopes_skipped: SkippedIntegrationScope[];
  integrations: EffectiveIntegrationProjection[];
  /**
   * ENG-10197 — every row excluded above, named in full with the reason.
   *
   * The counters stay, and stay authoritative for reconciliation; this is the
   * same set of removals expressed as rows instead of totals. `excluded.length`
   * equals `excluded_control_plane_only + excluded_unhealthy +
   * shadowed_by_more_specific + duplicate_merge_keys` by construction, and a
   * test pins that so the two representations can never drift into disagreeing
   * about what was removed.
   *
   * Sorted by `definition_id` then `connection_key`, matching `integrations`, so
   * the two lists read side by side.
   *
   * This does NOT change `effective_count` or `integrations`: the reconciliation
   * with `POST /host/agent-integrations` is exactly as it was.
   */
  excluded: ExcludedIntegrationProjection[];
}

export interface AlertDebugProjection {
  id: string;
  kind: string | null;
  severity: string | null;
  message: string | null;
  team_id: string | null;
  host_id: string | null;
  agent_id: string | null;
  source: string | null;
  opened_at: string | null;
  closed_at: string | null;
  closed_reason: string | null;
  acknowledged_at: string | null;
  snoozed_until: string | null;
  /**
   * ENG-10030 — is `message` current, or a snapshot?
   *
   * `open_alert` overwrites `message` on re-raise but never touches
   * `opened_at` (migration 20260613000001, `ON CONFLICT … DO UPDATE SET message
   * = EXCLUDED.message`). So a message is current only if that
   * `(kind, dedupe_key)` was re-raised since the alert opened; otherwise it is
   * frozen at `opened_at`, arbitrarily far behind the code that would produce
   * it today — and it reads like live output either way.
   *
   * ONE DIRECTION IS PROOF, THE OTHER IS A HINT. Do not read this as a
   * biconditional:
   *
   *   updated_at === opened_at  CONCLUSIVE. Never written since insert, so
   *                             never re-raised, so `message` is definitively
   *                             frozen at `opened_at`.
   *   updated_at  >  opened_at  SUGGESTIVE ONLY. `alerts_set_updated_at` is a
   *                             BEFORE UPDATE trigger on EVERY write — an
   *                             acknowledge, a snooze, a CLOSE, or a backfill
   *                             migration. Disambiguate against
   *                             `acknowledged_at` / `snoozed_until` /
   *                             `closed_at`: if `updated_at` matches one of
   *                             them, that write was the human action, not a
   *                             re-raise.
   *
   *                             MATCHING NONE OF THEM IS EVIDENCE, NOT PROOF.
   *                             A migration that touches the row bumps
   *                             `updated_at` and leaves no timestamp of its
   *                             own — `20260613000001_alerts_organization_id`
   *                             is exactly that shape. So the unmatched case
   *                             narrows the field; it does not close it, and
   *                             two reads remain the only positive proof.
   *
   * The naive reading is expensive rather than merely imprecise — 63 of 98 open
   * alerts on one measured board were acknowledged, so it would report ~64% of
   * them as carrying current text off the ack alone, and the frozen ones are
   * the aged, acked, nobody-watching rows where a stale message misleads most.
   *
   * The equality test is exact and needs no epsilon: both columns `DEFAULT
   * now()`, and `now()` is transaction-start time in Postgres, so they are
   * identical on insert.
   */
  updated_at: string | null;
  /**
   * ENG-10030 — the alert's identity, `<kind>:<subject id>`.
   *
   * WHAT IT ANSWERS THAT NOTHING ELSE HERE DOES: which ROW the alert is about.
   * `agent_id` names the agent, not the integration/host row, so without this
   * an alert frozen against a deleted subject looks exactly like one against a
   * live subject. ENG-9956 turns on precisely that question and had to be
   * shipped as an inference because this column was not on the wire.
   */
  dedupe_key: string | null;
}

/**
 * ENG-6483: one row of `debug_search_orgs` — a first-class lister for the
 * organizations a staff principal is authorized to read, so org-level triage and
 * access decisions are one call instead of inferring orgs off agent/host rows.
 *
 * `standing_reason` is WHY this org is visible to the caller — `internal`
 * (IL-owned), `fully_managed` (standing customer read), or `granted` (a
 * self-managed org reachable only via an active debug_grant). `has_active_grant`
 * is the orthogonal "do I hold a live grant right now" signal (true even on a
 * standing org with a redundant grant). The counts are diagnostic rollups; pure
 * metadata, same projection-not-raw-rows contract as the rest of the surface.
 */
export type OrgStandingReason = 'internal' | 'fully_managed' | 'granted';

export interface OrgDebugProjection {
  organization_id: string;
  organization_slug: string | null;
  display_name: string | null;
  is_internal: boolean;
  /** The org management mode: `fully_managed` | `self_managed`. */
  management_mode: string | null;
  /** Why this org is readable for the calling principal. */
  standing_reason: OrgStandingReason;
  /** Whether the caller currently holds an active (live, unexpired) debug grant for it. */
  has_active_grant: boolean;
  host_count: number;
  agent_count: number;
  active_agent_count: number;
  /** Open (unclosed) team-scoped alerts for the org. NULL-team infra alerts excluded. */
  open_alert_count: number;
  created_at: string | null;
}

/**
 * The fixed disclaimer shipped alongside `AgentDebugDetail.agent_integrations`.
 *
 * ENG-8271: the per-agent array is retained for compatibility, but a partial
 * read in a diagnostic tool is worse than a missing one — it produces confident
 * wrong answers. So the payload SAYS it is partial, in the payload itself,
 * rather than relying on a reader knowing the schema.
 */
export const AGENT_INTEGRATIONS_PARTIAL_NOTE =
  'Per-agent rows only — team- and org-scoped installs are NOT included. ' +
  'For the full set the agent actually runs with, read `effective_integrations` ' +
  '(each entry carries its `scope`) and `effective_integrations.effective_count`.';

/**
 * ENG-10145: whether the HITL-gated remedial tools can reach this agent at all.
 *
 * Every remedial tool (`debug_resume_agent`, `debug_restart_agent`,
 * `request_action`, `request_config_update`, `request_ssm_run`,
 * `debug_replay_dead_letter`, `request_clear_restart_request`) goes through
 * the same org WRITE check before any approval card exists. On a customer org
 * with no human-issued write grant, that check refuses with a 403: nobody is
 * paged and no request is filed. The tool descriptions used to say only
 * "a HUMAN must approve", so an agent composed a careful reason for an
 * approver who would never see it, and reported a pending approval that did
 * not exist.
 *
 * So the answer is carried in the read, computed by the SAME authorization
 * RPC the write handlers use, and the payload explains itself.
 */
export function orgWriteAuthorizedNote(available: boolean | null): string {
  if (available === true) {
    return (
      "This agent's org passes the write-authorization check every remedial tool runs, so the " +
      'org-ownership refusal (ENG-10145) will not fire. Write mode, token capability and approval ' +
      'routing are still checked at call time, so an approval card is not guaranteed.'
    );
  }
  if (available === false) {
    return (
      'Remedial actions are NOT reachable for this agent: its org is not write-authorized for you, ' +
      'so every HITL-gated tool is refused with a 403 before any approval card exists and nobody is ' +
      'paged (a 404 instead only if your read access to this org lapsed in between). ' +
      'debug_request_access cannot change this (it mints read grants only). Do not call a ' +
      'remedial tool or report a pending approval; escalate to an Integrity Labs operator with the ' +
      'agent id, the action you need and why.'
    );
  }
  return (
    'Remedial reachability could not be determined (the authorization check failed). This is ' +
    'indeterminate, not a refusal and not an authorization. A remedial tool re-checks server-side: ' +
    'a 403 or 404 from it is a final refusal, and a 500 "failed to verify write authorization" is the ' +
    'same indeterminate failure. In neither case report a pending approval; relay the result and escalate.'
  );
}

/**
 * Composite returned by `debug_get_agent`: the agent + its host + integrations
 * + recent alerts.
 *
 * ENG-8271 changed the integration half. `agent_integrations` (formerly
 * `integrations`) is unchanged in content but renamed to say what it is, and
 * carries `agent_integrations_note`. `effective_integrations` is the new
 * complete read — agent + team + org, with per-entry scope attribution — and
 * its `effective_count` reconciles with the manager's provisioned count.
 */
export interface AgentDebugDetail extends AgentDebugProjection {
  host: HostDebugProjection | null;
  /**
   * PER-AGENT rows only (reads `agent_integrations`). Kept under an honest name
   * so "X is absent" can no longer be misread as "X is not installed".
   * @see AGENT_INTEGRATIONS_PARTIAL_NOTE
   */
  agent_integrations: IntegrationDebugProjection[];
  /** Verbatim `AGENT_INTEGRATIONS_PARTIAL_NOTE` — the partial-read warning, in-payload. */
  agent_integrations_note: string;
  /** The complete three-scope set with scope attribution + reconcilable count. */
  effective_integrations: EffectiveIntegrationSet;
  recent_alerts: AlertDebugProjection[];
  /**
   * ENG-10145: does this agent's org pass the write-authorization check every
   * HITL-gated remedial tool runs first? `false` means a remedial call is
   * refused with a 403 before any approval card exists. `true` means only that
   * this one check passes (write mode, token capability and approval routing are
   * still checked at call time). `null` means the check itself failed; the read
   * still succeeds. Named for what is measured, not "remediable", on the
   * ENG-8271 precedent of honest field names.
   */
  org_write_authorized: boolean | null;
  /** Verbatim `orgWriteAuthorizedNote(org_write_authorized)`. */
  org_write_authorized_note: string;
}

/**
 * ENG-6518: the result of `debug_get_host` — the host-centric composite, the
 * mirror of `AgentDebugDetail` for host-wide incidents (e.g. an env drift hitting
 * every agent on the box). One read returns the host + every agent bound to it +
 * a rollup of alerts (the host's own infra alerts, including NULL-team ones, PLUS
 * each bound agent's alerts) + a version/restart rollup.
 *
 * The CC/framework version lives on the host projection itself (`framework_version`),
 * so the `rollup` adds only the two host-grain leverage signals from
 * HostVersionProjection: how many agents the host carries and how many times they
 * restarted in the recent window. Pure metadata — same projection-not-raw-rows
 * contract as the rest of the surface.
 */
/**
 * ENG-9200: how the reported Claude account was arrived at.
 *
 * - `last-observed-auth-tuple-change` — a real observation; the fingerprint is
 *   the account the host most recently switched TO.
 * - `no-observed-change` — the host has never been seen changing accounts, so
 *   there is nothing to report. This is NOT "the host has no account": the
 *   derivation is change-triggered by construction.
 * - `lookup-failed` — the audit read errored. A null here says nothing about the
 *   host, only about this call.
 */
export type ClaudeAccountDerivation =
  | 'last-observed-auth-tuple-change'
  | 'no-observed-change'
  | 'lookup-failed';

/**
 * ENG-9200: WHICH Claude account a host is authenticated as.
 *
 * `claude_auth_status` answers whether a host can call Claude. It does not answer
 * whose subscription absorbs the usage, and a host on the wrong account is
 * indistinguishable from one on the right account by every other signal we have.
 *
 * Derived from the `agent.restart` audit trail rather than reported by the host:
 * the fingerprint has been in that payload since agt-cli 0.28.474 and the fleet
 * minimum is 0.28.480, so this needs no client change and therefore reaches the
 * hosts pinned at 0.28.610 — which are exactly the ones that matter.
 *
 * A fingerprint is `sha256(accountUuid:orgUuid)` truncated to 12 hex. It
 * identifies the account WITHOUT naming a person; resolving it to an identity
 * requires the pairing flow to record the intended principal.
 */
export interface HostClaudeAccount {
  /** Account the host was last observed switching TO, or null — see `derivation`. */
  fingerprint: string | null;
  /** When that change was observed (ISO), or null. */
  observed_at: string | null;
  /** Account it switched FROM, when the audit row recorded one. */
  previous_fingerprint: string | null;
  /** Why `fingerprint` holds what it holds. Never infer "no account" from null. */
  derivation: ClaudeAccountDerivation;
}

export interface HostDebugDetail extends HostDebugProjection {
  /** Every agent currently bound to this host (host_agents). */
  agents: AgentDebugProjection[];
  /** Host infra alerts (incl. NULL-team) + each bound agent's alerts, newest first. */
  recent_alerts: AlertDebugProjection[];
  /** ENG-9200: which Claude account this host is authenticated as. */
  claude_account: HostClaudeAccount;
  rollup: {
    agent_count: number;
    /** `agent.restart` audit events across the host's agents within the window. */
    restart_count: number;
    restart_window_hours: number;
  };
}

/**
 * ENG-6517: where a host's effective value for ONE feature flag came from.
 * Mirrors the host runtime's own layering (`resolveFlagFromLayers`):
 * env override > heartbeat-materialized (the value the control plane last sent
 * the host) > compiled default. A resolved flag value is `boolean | string`
 * (the registry's `FlagValue`).
 */
export type InspectFlagSource = 'env' | 'heartbeat' | 'default';

export interface InspectFlagsEntry {
  key: string;
  /** The value the HOST is effectively running with (env > heartbeat > default). */
  effective: boolean | string;
  /** Where `effective` came from on the host. */
  source: InspectFlagSource;
  /** The host env-override value (from the heartbeat-reported env_gates), or null. */
  env_value: boolean | string | null;
  /** The env var that overrides this flag on the host, or null if none exists. */
  env_var: string | null;
  /** The value the host last RECEIVED from the control plane (latest snapshot), or null. */
  heartbeat_value: boolean | string | null;
  /** The control plane's CURRENT resolved value for this host scope. */
  central_value: boolean | string;
  /** The compiled registry default. */
  default_value: boolean | string;
  /**
   * True when an env override is masking a DIFFERENT heartbeat-resolved value —
   * the ENG-6478 drift class (an env gate silently overriding the DB-resolved flag).
   */
  env_masks_heartbeat: boolean;
  /** True when the host's last-received value differs from the current central value (host stale). */
  host_stale: boolean;
  sensitive: boolean;
}

/**
 * ENG-6517: the result of `debug_inspect_flags` — an agent/host's EFFECTIVE
 * feature flags WITH source attribution, so the "an env override masked a
 * heartbeat flag" class of drift (ENG-6478) is a one-call lookup instead of
 * WARN-log archaeology. The host-side env/heartbeat values come from the latest
 * `host_config_snapshots` row (ENG-6412); `central_value` is the control plane's
 * current resolution (`getEvaluatedFlags`) for staleness comparison.
 */
export interface InspectFlagsProjection {
  host: { id: string; name: string | null; organization_id: string | null };
  /** The agent the lookup was resolved through, when called with `agent_id`. */
  via_agent_id: string | null;
  /** Latest host config snapshot meta, or null when the host has never reported one. */
  snapshot: {
    captured_at: string;
    config_hash: string;
    flags_schema_version: string | null;
    agt_cli_version: string | null;
  } | null;
  flags: InspectFlagsEntry[];
  /** Keys exhibiting drift (env_masks_heartbeat OR host_stale) — the leverage signal. */
  drift_keys: string[];
  /** The registry schema version the API is running (compare against the snapshot's). */
  flags_schema_version: string;
}

/**
 * ENG-6516: the result of an alert-triage write (debug_ack_alert /
 * debug_snooze_alert / debug_close_alert). Unlike the host-affecting remedial
 * actions (restart, ssm_run, …) these are LOW-RISK control-plane DB mutations on
 * `alerts` state columns — reversible, no customer-host effect — so they are a
 * direct write gated by `ADMIN_DEBUG_WRITE_MODE` + org write-authorization +
 * audit, NOT the Slack-approval machinery (the webapp acks/snoozes the same way).
 *
 * `applied` is false in `shadow` mode (the gate ran, nothing was written). The
 * `alert` projection reflects the post-write state in `enforce` mode, or the
 * current state in `shadow`.
 */
export type AlertTriageAction = 'ack' | 'snooze' | 'close';

export interface AlertTriageResult {
  alert_id: string;
  action: AlertTriageAction;
  /** off ⇒ refused upstream (503); shadow ⇒ no write; enforce ⇒ written. */
  write_mode: 'shadow' | 'enforce';
  /** True only when the row was actually mutated (enforce). */
  applied: boolean;
  alert: {
    id: string;
    organization_id: string | null;
    kind: string;
    severity: string | null;
    acknowledged_at: string | null;
    acknowledged_by: string | null;
    snoozed_until: string | null;
    closed_at: string | null;
    closed_reason: string | null;
  };
}

/**
 * ENG-6431 (#4): one row of `debug_host_versions` — a fleet-wide health
 * snapshot per host. Reuses the host projection (framework_version is the CC
 * version the MANAGER sees, not any agent's — see the field doc, it is a
 * genuine trap) and adds the two leverage signals an SRE asked for: how many agents
 * the host carries and how many times they restarted in the recent window
 * (a high `restart_count` is the tell for a thrashing host). Pure metadata —
 * same projection-not-raw-rows contract as the rest of the surface.
 */
export interface HostVersionProjection {
  id: string;
  name: string | null;
  organization_id: string | null;
  status: string | null;
  framework: string | null;
  /**
   * The Claude Code version resolvable on the MANAGER PROCESS'S OWN PATH, as of
   * the host's last heartbeat.
   *
   * READ THIS BEFORE QUOTING IT AS EVIDENCE. It is produced by
   * `execFile('claude', ['--version'])` run inside the manager's environment
   * (`frameworks/claudecode/index.ts`, `getVersion()`). The manager never enters
   * an agent's container, so this number says NOTHING about what version any
   * agent on this host is actually running. Agents run in their own containers
   * and can drift arbitrarily far from the manager's binary.
   *
   * The old wording here was "the Claude Code / framework version the host last
   * reported", which is not false and is exactly ambiguous enough to be misread
   * as per-agent evidence. It was, in an operator report, by me. If the question
   * is "what is THIS AGENT running", this field cannot answer it and no amount
   * of care in reading it will make it able to.
   *
   * WHY IT LOOKS LIKE A CHANNEL NUMBER RATHER THAN AN INSTALL, which is the
   * part that misled two separate investigations (ENG-9447):
   *
   * The fleet was observed moving 17 hosts' `framework_version` in lockstep the
   * moment npm `latest` went 2.1.245 → 2.1.246, which reads as conclusive
   * evidence that the field is the channel head echoed back with nothing
   * installed. It is not. `claude-code-updater.ts` runs
   * `npm install -g @anthropic-ai/claude-code@<target>` on the host, where
   * `target` defaults to the `latest` dist-tag. So the number IS a real
   * installed binary — the host keeps its own install pinned to the channel
   * head, and therefore the whole fleet moves together.
   *
   * Both readings of the evidence are wrong in the same practical direction, so
   * the distinction matters: "nothing is installed" and "the host's install is
   * current" are opposite claims about the host, and neither tells you anything
   * about an agent. Agent containers do not run that updater; they carry
   * whatever their image shipped, which was measured 26 releases behind.
   *
   * (This also explains the apparent impossibility on `two-tractors-host`: it
   * reports the newest release while unable to self-update. The wedged latch is
   * the agt-cli updater, a different mechanism from the claude-code one.)
   *
   * One further subtlety, because it surprises people who go looking: the
   * manager picks the framework ADAPTER from `state.agents[0]`
   * (`manager-worker.ts`, "Use the first known agent's framework") but then
   * executes the version probe in its own environment regardless. So the
   * adapter choice is per-first-agent while the measurement is manager-local.
   */
  framework_version: string | null;
  /**
   * ENG-8341: the agt CLI version the host last reported. Despite this tool
   * being called `debug_host_versions`, it carried no agt-cli information at
   * all before this - which is why a 44-hour update gap on `my-second-host`
   * went unnoticed, and why the ENG-8343 investigation could not answer the one
   * question it turned on.
   */
  agt_version: string | null;
  /** ENG-8341: the operator pin, NULL when unpinned. Drift is only meaningful against it. */
  desired_agt_cli_version: string | null;
  last_seen_verdict: LivenessVerdict;
  last_seen_age_seconds: number | null;
  /** Agents currently bound to this host (host_agents). */
  agent_count: number;
  /** `agent.restart` audit events for this host's agents within the window. */
  restart_count: number;
  /**
   * ENG-9430: the same distinct events as `restart_count`, split by SOURCE and
   * ordered with the dominant cause first.
   *
   * WHY IT MATTERS THAT THIS EXISTS. `restart_count` is described by this
   * tool's own docs as "the tell for a thrashing host", and operators read it
   * that way. But most restarts are not trouble. Measured on
   * `two-tractors-host`, 2026-08-25, 24h: `managed-mcp-churn` 8,
   * `hot-reload-mcp` 5, `day-rollover` 9, `credential-rotation` 1. Fourteen of
   * the twenty-five are an operator changing configuration — the system working
   * — and one number cannot say so. "25 restarts in 24h" reads as a host in
   * trouble, and it was used that way, including in two operator reports as
   * evidence for a diagnosis it did not support.
   *
   * Keys are the source string AS WRITTEN, not the `RestartSource` union: real
   * production sources (`managed-mcp-churn`, `credential-rotation`) are absent
   * from that union, and validating against it would drop the very rows this
   * exists to surface. Unsourced rows appear under a distinct sentinel rather
   * than being merged into a named cause.
   *
   * Empty object when the host had no restarts in the window — never absent, so
   * a reader never has to distinguish "no restarts" from "field not returned".
   */
  restart_by_source: Record<string, number>;
  /** The window `restart_count` was computed over, in hours (default 24). */
  restart_window_hours: number;
  /**
   * ENG-9664: why this host is NOT expected to be alive, or null when it is.
   * Same field, same union and same reasoning as `HostDebugProjection` above.
   *
   * It matters MORE here than there, and `lkg-1` is why. This is the version
   * tool, so it is the surface an operator uses to ask "which hosts still need
   * updating" — and a terminated host answers that question as an outstanding
   * one. The cohort read "10 fixed, 1 outstanding" when the truth was "10
   * fixed, 1 that no longer exists", and the obvious next action was to go and
   * unpin a machine that is gone.
   */
  torn_down_reason: 'decommissioned' | 'terminating' | 'terminated' | null;
}

/**
 * ENG-6431 (#2): the result of `debug_tail_logs` — the trailing lines of ONE
 * allowlisted host log for an authorized agent, fetched live over SSM.
 *
 * Unlike the rest of the surface this DOES carry payload content (`content` is
 * the raw log tail), because a log tail IS the diagnostic — the projection
 * principle ("never raw DB rows") is upheld differently here: the readable set
 * is a fixed allowlist of OPERATIONAL logs (manager.log, pane.log, channel-MCP
 * stderr, manager-state.json) under `~/.augmented/<code_name>/`. Secret-bearing
 * files (`.mcp.json`, `.env.integrations`) are NOT in the allowlist and cannot
 * be reached through this tool. The read is org-walled + audited like every
 * other debug read.
 */
export interface TailLogsProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The host the tail ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** The requested log key (e.g. `manager`, `pane`). */
  log: string;
  /** The resolved relative filename under `~/.augmented/<code_name>/`. */
  log_file: string;
  /** Trailing lines requested (after clamp). */
  lines_requested: number;
  /** False when the file did not exist on the host (content is then ''). */
  log_present: boolean;
  /** The log tail (UTF-8, newest bytes kept when byte-clipped). */
  content: string;
  bytes_returned: number;
  /** True when the tail was clipped to the byte cap (oldest content dropped). */
  truncated: boolean;
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  /** Null when no SSM command ran (e.g. no host binding → status `skipped`). */
  ssm_command_id: string | null;
}

/**
 * ENG-6515: the result of `debug_query_logs` — a TIME-WINDOWED read of ONE
 * allowlisted host log, spanning the active file AND its rotated siblings
 * (`manager.log.1`, `manager.log.2.gz`, … — logrotate keeps 14, gzipped). Where
 * `debug_tail_logs` only sees the live tail of the current file (minutes, and
 * gone once it rotates), this resolves the "did it restart at 4pm yesterday?"
 * class of question by reading across rotation boundaries and filtering lines to
 * a `[since, until]` window.
 *
 * Same allowlist + org-wall + audit contract as `debug_tail_logs`: only the
 * fixed set of OPERATIONAL logs is reachable (secret-bearing files are not), the
 * read is org-walled, and every call is audited as a cross-org host access. It
 * carries `content` for the same reason tail does — the log lines ARE the
 * diagnostic.
 *
 * Time filtering keys off the manager's ISO8601 line prefix
 * (`[manager-worker 2026-06-15T14:32:45.123Z] …`); lines without a parseable
 * timestamp inherit the in-window state of the preceding timestamped line (so
 * multi-line entries survive), and a log that carries no timestamps at all
 * (e.g. `pane`, `manager-state`) returns its byte-capped tail unfiltered —
 * `time_filtered` reports which happened.
 */
export interface QueryLogsProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The host the read ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** The requested log key (e.g. `manager`, `pane`). */
  log: string;
  /** The resolved filename (e.g. `manager.log`). */
  log_file: string;
  /** The applied lower bound (normalized ISO8601 UTC, `YYYY-MM-DDTHH:MM:SS`). */
  since: string;
  /** The applied upper bound (normalized ISO8601 UTC, `YYYY-MM-DDTHH:MM:SS`). */
  until: string;
  /** Max lines returned (after clamp; newest kept when clipped). */
  lines_requested: number;
  /** Whether reliable ISO8601 line-timestamp filtering applies to this log. */
  time_filtered: boolean;
  /** False when neither the active file nor any rotated sibling existed. */
  log_present: boolean;
  /** The matched log content (UTF-8, newest bytes kept when byte-clipped). */
  content: string;
  bytes_returned: number;
  /** True when the result was clipped to the byte/line cap (oldest content dropped). */
  truncated: boolean;
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  /** Null when no SSM command ran (e.g. no host binding → status `skipped`). */
  ssm_command_id: string | null;
}

/**
 * ENG-6431 (#1, PR B): the result of `debug_probe_integration` — a LIVE
 * connectivity verdict for ONE installed integration, produced by SSM-invoking
 * the host primitive `agt integration probe <code_name> <slug> --json`
 * (ENG-6441) on the agent's current host. This is NOT the cached
 * `last_connectivity_status` the central `POST /integrations/:id/test` echoes;
 * the probe runs fresh on the host (the only place the agent's wired
 * `.mcp.json` + `.env.integrations` exist), so the verdict is ground truth.
 *
 * `verdict` is the host probe's `ConnectivityStatus` (ok | degraded |
 * transient_error | down) or `not_probeable` when no probe is wired for that
 * integration kind — PLUS the central-derived non-verdicts below when no clean
 * host verdict was produced:
 *  - `unreachable`      — the agent has no current host binding / no instance id.
 *  - `not_installed`    — the host reported the integration isn't installed.
 *  - `host_cli_too_old` — the host's agt-cli predates `agt integration probe`.
 *  - `probe_error`      — SSM ran but the verdict couldn't be obtained (timeout,
 *                         non-zero exit, unparseable output).
 * Org-walled + audited like every other host-reaching debug read.
 */
export type ProbeIntegrationVerdict =
  | 'ok'
  | 'degraded'
  | 'transient_error'
  | 'down'
  /**
   * ENG-9122: the probe ran and reported that it did not MEASURE anything -
   * the credential broker could not supply a token, the shim could not find
   * the real binary, the leg was a no-op. `ConnectivityStatus` has carried
   * this value the whole time; this union and `HOST_PROBE_STATUSES` did not,
   * so every one of these host verdicts was discarded as `probe_error` with
   * the message `{`. It is NOT a verdict about the integration's health.
   */
  | 'unverified'
  | 'not_probeable'
  | 'not_installed'
  | 'host_cli_too_old'
  | 'probe_error'
  | 'unreachable';

/**
 * ENG-8352: marker the SSM command block prints to stderr when it could not
 * assemble the env `agt` needs (AGT_HOST / AGT_API_KEY / AGT_TEAM). Shared here
 * so the route that EMITS it and the resolver that CLASSIFIES it can never drift
 * apart into a string mismatch that silently reopens this bug.
 */
export const PROBE_ENV_SENTINEL = 'AGT_PROBE_ENV_INCOMPLETE:';

/**
 * The host probe statuses that come straight back from `agt integration probe --json`.
 *
 * ENG-9122: this set MUST stay a superset of `ConnectivityStatus`
 * (packages/core/src/integrations/connectivity-probe.ts), which is the type the
 * host primitive actually emits, plus `not_probeable` which only this lane
 * produces. It was not: `unverified` had been in `ConnectivityStatus` for
 * months and was never added here.
 *
 * The consequence was not a missing label. An unrecognised status falls all the
 * way through `resolveProbeVerdict` to its generic `probe_error` branch, whose
 * message used to be `firstLine(stdout)` - and the host emits PRETTY-PRINTED
 * JSON, so the first line is the single character `{`. That is the entirety of
 * "every integration probe on agt-aws-1 returns probe_error with the message
 * `{`": the payload was complete and well-formed, carried a real diagnosis, and
 * was thrown away for being honest about not having measured anything.
 *
 * Cost: 8 of 24 open criticals on the fleet, streaks to 562, six weeks. There
 * is a test asserting the superset relation so the next value added to
 * `ConnectivityStatus` cannot silently reopen this.
 */
export const HOST_PROBE_STATUSES: ReadonlySet<string> = new Set([
  'ok',
  'degraded',
  'transient_error',
  'down',
  'unverified',
  'not_probeable',
]);

export interface ProbeIntegrationProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The integration slug (definition code_name) that was probed. */
  slug: string;
  /**
   * ENG-8227: set when the caller passed a BARE toolkit name (`hubspot`) that
   * was expanded to the namespaced installed identifier (`composio/hubspot`).
   * Present only on that path, so a verdict is never silently attributed to a
   * slug the caller did not type.
   */
  slug_resolved_from?: string;
  /** The host the probe ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** The live connectivity verdict, or a central-derived non-verdict (see above). */
  verdict: ProbeIntegrationVerdict;
  /** Human-readable detail from the host probe, or why no verdict was produced. */
  message: string | null;
  /** ISO timestamp the host stamped the probe (null when no host verdict). */
  probed_at: string | null;
  /**
   * ENG-10646 — WHICH CONNECTION THIS VERDICT IS ABOUT.
   *
   * Since ENG-7543 a toolkit can hold several rows under one `definition_id`,
   * distinguished by `connection_key`, and the host primitive probes the FIRST
   * it resolves. ENG-10488 made it say so: `agt integration probe --json` has
   * emitted `connection_key`, `sibling_connection_keys` and `scope_note` on
   * every verdict since, and the comment above its `connection_key` names THIS
   * tool as the consumer that needs them — "a consumer (the admin-debug tool
   * that shells this over SSM included) cannot tell a single-connection
   * integration from the first of several."
   *
   * That consumer did not read them. The scope was computed on the host,
   * serialised, shipped over SSM, and dropped one function short of the
   * operator. Measured: agent `ea` holds `composio/outlook` twice — `default`
   * (active, ok) and `work` (error, "No credentials configured", 392
   * consecutive failures, an integration_down alert open against it since 13
   * September). A forced probe returned `ok, "287 tools"` with nothing saying
   * which row that was about, so the honest reading of a 392-failure alert
   * against a live `ok` is "monitoring artefact" — closing a dead integration
   * as noise. `ok` is the answer nobody re-reads.
   *
   * Null/empty when the host's CLI predates ENG-10488. Hosts defer CLI updates
   * to a maintenance window and the fleet runs several versions at once, so
   * absence here means "this host did not say", never "there is one connection".
   */
  connection_key?: string | null;
  /** Other `connection_key`s under this slug that the host could SEE. See below. */
  sibling_connection_keys?: string[];
  /**
   * The host's own statement of what this verdict does and does not cover.
   *
   * Carried verbatim rather than reconstructed here, because the fact that
   * makes it matter is only knowable on the host: `POST /host/agent-integrations`
   * filters to status active/configured, so a sibling in `error` or `unhealthy`
   * — precisely the sibling worth knowing about — is absent from the set the
   * probe searched and CANNOT appear in `sibling_connection_keys`. An empty
   * sibling list therefore does not mean there is only one connection, and the
   * note is what says so.
   */
  scope_note?: string | null;
  /**
   * Fields the host DID send that this version could not read (obi-wan
   * `0f7c98681bb8` on #6203). Absence of `connection_key` is documented above
   * as "the host did not say", so silently collapsing a wrong-SHAPED value into
   * absence would report an up-to-date host as an old one, with nothing logged
   * at either end. Naming the field here keeps "did not say" and "said
   * something we could not parse" distinguishable, which is the whole point of
   * the contract they sit under.
   */
  scope_unreadable?: string[];
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  ssm_command_id: string | null;
}

/** Raw host-command result the verdict resolver classifies (decoupled from SSM types). */
export interface RawHostProbeResult {
  stdout: string;
  stderr: string;
  responseCode: number | null;
  timedOut: boolean;
}

export interface ProbeVerdictResolution {
  verdict: ProbeIntegrationVerdict;
  message: string | null;
  probed_at: string | null;
  /** ENG-10646: the host's scope fields, carried through. Absent on every
   *  non-JSON branch below (there is no host payload to read them from) and on
   *  a host whose CLI predates ENG-10488. */
  connection_key?: string | null;
  sibling_connection_keys?: string[];
  scope_note?: string | null;
  /** Names of scope fields the host sent in a shape this version cannot read. */
  scope_unreadable?: string[];
}

/**
 * ENG-10646 — the host scope fields, pulled off the parsed probe payload.
 *
 * Separate and exported so the parity guard can assert the whole set in one
 * place. The defect this closes was not a wrong line, it was a MISSING one: the
 * resolver read three fields and the host wrote six, and nothing anywhere
 * compared the two lists. ENG-9588 is the same shape on the other lane ("the
 * forced lane has silently dropped a field the scheduled lane carries three
 * times... each time the omission was unreachable from a test").
 */
export function extractProbeScope(
  parsed: Record<string, unknown>,
): Pick<
  ProbeVerdictResolution,
  'connection_key' | 'sibling_connection_keys' | 'scope_note' | 'scope_unreadable'
> {
  // PRESENT-BUT-UNREADABLE IS NOT ABSENT (obi-wan `0f7c98681bb8` on #6203).
  //
  // The contract this feeds says absence means "the host's agt-cli predates
  // ENG-10488 — this host did not say". So if the CLI ever changes one of these
  // fields' TYPE (to `{key, status}[]`, say — a change the parity guard cannot
  // see, because it pins key NAMES), dropping the value would report an
  // up-to-date host that DID say as one that did not, silently, at both ends.
  // Every unreadable field is named instead.
  const unreadable: string[] = [];
  const present = (k: string): boolean => Object.prototype.hasOwnProperty.call(parsed, k);

  // `null` is a MEANINGFUL value the host sends for a row carrying no
  // connection_key, and it must survive as null rather than collapsing to
  // undefined — "this row has no key" and "this host did not say" are
  // different facts and the operator acts differently on them.
  const rawKey = parsed['connection_key'];
  const connection_key =
    typeof rawKey === 'string' || rawKey === null ? (rawKey as string | null) : undefined;
  if (connection_key === undefined && present('connection_key')) unreadable.push('connection_key');

  const siblings = parsed['sibling_connection_keys'];
  let sibling_connection_keys: string[] | undefined;
  if (Array.isArray(siblings)) {
    sibling_connection_keys = siblings.filter((k): k is string => typeof k === 'string');
    // The element filter has the same defect in miniature: four siblings
    // arriving with two malformed entries would be reported as two, which
    // under-reports in exactly the direction this PR's own copy warns operators
    // about ("an empty list is not evidence there is only one connection").
    if (sibling_connection_keys.length !== siblings.length) {
      unreadable.push('sibling_connection_keys');
    }
  } else if (present('sibling_connection_keys')) {
    unreadable.push('sibling_connection_keys');
  }

  // Same rule as `connection_key` above, and it was missing here (obi-wan
  // `f9c6f7c425f2` on #6203). `scope_note` is declared `string | null` on both
  // `ProbeIntegrationProjection` and `ProbeVerdictResolution`, so `null` is a
  // documented host value, not a malformed one. Accepting only `string` made a
  // conforming host report as `scope_unreadable` — which this contract defines
  // as "the host DID send it in a shape this version cannot read", i.e. a parse
  // failure an operator should escalate. Two fields under one contract must not
  // disagree about what the contract permits.
  const rawNote = parsed['scope_note'];
  const scope_note =
    typeof rawNote === 'string' || rawNote === null ? (rawNote as string | null) : undefined;
  if (scope_note === undefined && present('scope_note')) unreadable.push('scope_note');

  return {
    connection_key,
    sibling_connection_keys,
    scope_note,
    ...(unreadable.length > 0 ? { scope_unreadable: unreadable } : {}),
  };
}

/** First non-empty line of a blob, trimmed and length-capped — for a tidy message. */
function firstLine(s: string, max = 300): string {
  const line = (s.split('\n').find((l) => l.trim().length > 0) ?? '').trim();
  return line.length > max ? line.slice(0, max) : line;
}

/**
 * Credential-shaped substrings, redacted before raw probe output is quoted back.
 *
 * `unparseableSnippet` widens what reaches an operator from one line to the
 * whole blob, and a blob we could not parse is by definition a blob we do not
 * know the shape of — so it must not be assumed safe. Same pattern set as
 * `apps/cli/src/lib/cli-probe.ts`, which redacts for the same reason on the
 * other side of the wire.
 */
const CREDENTIAL_SHAPED =
  /\b(gh[pousr]_[A-Za-z0-9]{16,}|github_pat_[A-Za-z0-9_]{20,}|xox[abprs]-[A-Za-z0-9-]{10,}|sk-[A-Za-z0-9]{20,}|eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}|AKIA[0-9A-Z]{16})\b/g;

/**
 * ENG-9122 AC2 — the output a `probe_error` could not make sense of, rendered
 * so it is actually diagnosable.
 *
 * `firstLine` is right for stderr, where a CLI puts one reason on one line. It
 * is catastrophically wrong for stdout, because our own host primitive emits
 * `JSON.stringify(result, null, 2)`: the first non-empty line of a pretty-
 * printed object is `{`, and that single character was the ENTIRE message on
 * every failed probe on `agt-aws-1`. The payload it came from named the exact
 * problem in a full sentence. We had the answer and printed a brace.
 *
 * So: collapse whitespace (a multi-line object becomes one readable line),
 * redact anything credential-shaped, and bound the length. Truncation is marked
 * with `…` so a reader can tell a clipped payload from a short one.
 */
function unparseableSnippet(s: string, max = 400): string {
  const compact = s.replace(/\s+/g, ' ').trim();
  const redacted = redactCredentials(compact);
  return redacted.length > max ? `${redacted.slice(0, max - 1)}…` : redacted;
}

/**
 * Strip credential-shaped substrings from anything about to be quoted back to
 * an operator.
 *
 * Split out of {@link unparseableSnippet} so the stderr path can share it. The
 * regex is `/g` and therefore stateful via `lastIndex`; `String.replace` resets
 * it on entry, so calling this twice in a row is safe — but that is a property
 * of `replace`, not of the regex, and inlining `.test()` or `.exec()` anywhere
 * against `CREDENTIAL_SHAPED` would not be.
 */
function redactCredentials(s: string): string {
  return s.replace(CREDENTIAL_SHAPED, '<redacted>');
}



/**
 * Find the host primitive's `--json` object in stdout.
 *
 * ENG-8352: the previous implementation tried the whole trimmed blob, then fell
 * back to "the LAST line that parses to an object". That fallback could NEVER
 * fire for our own producer: `agt integration probe --json` emits
 * `JSON.stringify(result, null, 2)` (apps/cli/src/lib/globals.ts), i.e. PRETTY-
 * PRINTED multi-line JSON, and no single line of a pretty-printed object is
 * itself a complete object. So the moment anything at all contaminated stdout —
 * a stray log line, a spinner artifact, a trailing newline of noise — the whole-
 * blob parse failed, the line fallback was structurally incapable of recovering,
 * and a perfectly good verdict was discarded as `probe_error` whose message was
 * `firstLine(stdout)` — the single character `{`. That was observed live on
 * everperform/vera/firecrawl with ssm_status Success: the host probe RAN,
 * produced a real verdict, and we threw it away.
 *
 * We now brace-match: scan for balanced `{...}` regions (respecting string
 * literals and escapes so a brace inside a message can't skew the depth count)
 * and parse each candidate. Fixing the PARSER rather than constraining the
 * producer means any future producer — compact, pretty, or noisy — is handled.
 *
 * Candidate preference is deliberate: the LAST object carrying a string `status`
 * wins, because the verdict is emitted after any leading log noise, and a probe
 * object is identified by its `status` key rather than by position.
 */
function parseProbeJson(stdout: string): Record<string, unknown> | null {
  const asObject = (s: string): Record<string, unknown> | null => {
    try {
      const v = JSON.parse(s) as unknown;
      return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  };

  // Fast path: the blob is exactly one JSON object (the common, clean case).
  const trimmed = stdout.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    const whole = asObject(trimmed);
    if (whole) return whole;
  }

  // Brace-match every balanced `{...}` region in the blob.
  const candidates: Record<string, unknown>[] = [];
  for (let start = stdout.indexOf('{'); start !== -1; start = stdout.indexOf('{', start + 1)) {
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < stdout.length; i++) {
      const ch = stdout[i]!;
      if (escaped) {
        escaped = false;
        continue;
      }
      if (inString) {
        if (ch === '\\') escaped = true;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') {
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0) {
          const parsed = asObject(stdout.slice(start, i + 1));
          if (parsed) candidates.push(parsed);
          break;
        }
      }
    }
  }
  if (candidates.length === 0) return null;

  // Prefer the last candidate that looks like a probe result.
  for (let i = candidates.length - 1; i >= 0; i--) {
    if (typeof candidates[i]!['status'] === 'string') return candidates[i]!;
  }
  return candidates[candidates.length - 1]!;
}

/**
 * Classify a raw host-probe command result into a verdict. Pure (no I/O) so it
 * is unit-testable without SSM. A clean host verdict (parsed `--json` with a
 * known status) passes straight through; everything else is mapped to a
 * central-derived non-verdict (old CLI, not-installed, timeout, parse/exit error)
 * so the tool degrades gracefully rather than crashing — AC: "graceful
 * degradation on an old-CLI host".
 */
/**
 * ENG-10196: matches the host CLI's "slug not found" stderr in BOTH spellings -
 * the pre-10196 `is not installed on <agent>` and the current `is not in
 * <agent>'s PROVISIONED set`. See the not_installed branch for why both.
 */
export const PROVISIONED_SET_MISS = /is not installed on|is not in \S+'s PROVISIONED set/i;

/**
 * Appended to every `not_installed` message. The list behind the verdict is the
 * provisioning set, so absence from it is not absence from the agent: a row in
 * status `error` or `unhealthy` is excluded while remaining probeable. Measured
 * on maven 2026-09-07 - composio/googleads and composio/webflow probed `ok`
 * (28 and 59 tools) seconds after being "not installed".
 */
export const NOT_IN_PROVISIONED_SET_CAVEAT =
  "(Not proof of absence: this is the PROVISIONED set, which excludes rows in status 'error' or " +
  "'unhealthy' that can still be present and probeable - read the agent's integration rows before " +
  'concluding it is not installed. ENG-10196)';

export function resolveProbeVerdict(r: RawHostProbeResult): ProbeVerdictResolution {
  const parsed = parseProbeJson(r.stdout);
  if (parsed && typeof parsed['status'] === 'string' && HOST_PROBE_STATUSES.has(parsed['status'])) {
    return {
      verdict: parsed['status'] as ProbeIntegrationVerdict,
      message: typeof parsed['message'] === 'string' ? parsed['message'] : null,
      probed_at: typeof parsed['probed_at'] === 'string' ? parsed['probed_at'] : null,
      // ENG-10646: the scope travels with the verdict. This is the only branch
      // with a host payload to read it from.
      ...extractProbeScope(parsed),
    };
  }

  if (r.timedOut) {
    return {
      verdict: 'probe_error',
      message: 'the probe did not return a verdict before the poll ceiling',
      probed_at: null,
    };
  }

  const stderr = r.stderr ?? '';
  // Commander prints `error: unknown command 'probe'` (or `unknown option`) on a
  // CLI that predates `agt integration probe`. That is the old-CLI tell.
  if (/unknown command|unknown option|did you mean|see --help/i.test(stderr)) {
    return {
      verdict: 'host_cli_too_old',
      message:
        "the host's agt-cli predates `agt integration probe` — update the host CLI to a build that carries it (ENG-6441)",
      probed_at: null,
    };
  }
  // The host primitive's not-installed error path (stderr, no JSON).
  //
  // ENG-10196: TWO spellings, and both must keep mapping here. The CLI used to
  // print `Integration "x" is not installed on <agent>. Installed: ...`; it now
  // prints `Integration "x" is not in <agent>'s PROVISIONED set.` because the
  // list it enumerates is the provisioning set (status active/configured), and
  // `error`/`unhealthy` rows are missing from it while staying probeable
  // (PROBE_RESCUE_STATUSES). Hosts defer CLI updates to a maintenance window, so
  // the fleet runs both spellings at once for hours at a time.
  //
  // Matching only the new form would have been worse than the bug it fixes:
  // every missing slug on an updated host would fall through to `probe_error`,
  // which is ENG-8352's "the prober failed" - the opposite misreading.
  //
  // The verdict keeps its name (the union is a public contract, as the ENG-8352
  // note below says). What changes is the MESSAGE, and for both spellings: an
  // old CLI's "not installed" rests on exactly the same incomplete list, so the
  // caveat is appended here, where it reaches every host regardless of version.
  if (PROVISIONED_SET_MISS.test(stderr)) {
    return {
      verdict: 'not_installed',
      message: `${firstLine(stderr)} ${NOT_IN_PROVISIONED_SET_CAVEAT}`,
      probed_at: null,
    };
  }

  // ENG-8352: the prober could not AUTHENTICATE, so it never reached the
  // integration at all. Two tells, both meaning "this says nothing about the
  // integration's health":
  //
  //  1. PROBE_ENV_SENTINEL — emitted by the SSM preflight when the command block
  //     could not assemble AGT_HOST/AGT_API_KEY/AGT_TEAM for the CLI.
  //  2. `Agent "<name>" not found.` — what the CLI prints when it runs WITHOUT a
  //     team scope, because the agent lookup is team-scoped. Observed live on
  //     agt-aws-1 for stirling/linear and marlow/image-gen while both agents were
  //     active with fresh heartbeats, so "not found" was never true of the agent.
  //
  // Both previously fell through to the generic `probe_error` below, which made a
  // dead prober indistinguishable from a broken integration — the silent failure
  // this ticket is about. The verdict stays `probe_error` (the union is a public
  // contract), but the message now says plainly that the PROBER failed, so nobody
  // reads it as a verdict about the integration.
  if (stderr.includes(PROBE_ENV_SENTINEL) || /Agent ".*" not found/i.test(stderr)) {
    const missing = stderr.match(new RegExp(`${PROBE_ENV_SENTINEL}\\s*([A-Z_, ]+)`))?.[1]?.trim();
    return {
      verdict: 'probe_error',
      message:
        'PROBER FAILED, NOT THE INTEGRATION — the host probe could not authenticate' +
        (missing ? ` (missing on host: ${missing})` : ' (no team scope on host)') +
        '. This is not a verdict about the integration; its real state is unknown. See ENG-8352.',
      probed_at: null,
    };
  }

  // ENG-9122 AC2: carry the output we could not parse. stderr keeps `firstLine`
  // (one reason on one line is exactly what a CLI writes there); stdout switches
  // to the whole compacted blob, because stdout is where our own pretty-printed
  // JSON lands and its first line carries no information at all.
  //
  // The prefix matters as much as the payload. `probe_error` with a bare quote
  // of some text reads as a verdict about the integration; it is not one, and
  // eight criticals were opened on the fleet because nothing said so.
  //
  // stderr is redacted too (CodeRabbit). The original code only ran the
  // credential filter over stdout, on the reasoning that the widened stdout
  // quote was the new exposure. That reasoning was wrong in a way worth naming:
  // stderr is where a CLI writes the failing command, and a failing command is
  // exactly where a token ends up - a curl echoing a signed URL, a `gh` dumping
  // the request it could not authenticate. Narrow output is not safe output,
  // and this branch reaches an operator either way.
  //
  // ENG-8352: stderr does NOT get to discard stdout. The ternary below used to
  // be a strict precedence — any stderr at all won, and the stdout payload was
  // dropped without trace. That is the same defect ENG-9122 fixed, wearing a
  // different truncation source: one stray line on stderr (an ora artifact, a
  // deprecation warning, a `gh` notice) is enough to throw away the entire body
  // the host actually produced, and the operator is left holding the least
  // informative half of a two-part answer.
  //
  // It is not hypothetical on this path. The probe's own writer pretty-prints to
  // stdout (`jsonOutput` → `JSON.stringify(x, null, 2)`), so when a body is
  // present it is the substantive half; stderr on the same run is usually a
  // banner. Preferring the banner is exactly backwards.
  //
  // So when both exist, BOTH are carried: the stderr line as the reason, the
  // stdout blob as the payload. Both are already redacted and capped
  // (`firstLine` at 300, `unparseableSnippet` at 400), so this cannot become
  // unbounded, and the "says nothing about the integration" clause is repeated
  // in every arm rather than only the stdout one — it is the sentence that
  // stopped eight criticals being opened against healthy integrations, and it
  // must not depend on which stream happened to be empty.
  //
  // WHEN BOTH STREAMS SPOKE, BOTH ARE CARRIED - but under a tighter per-stream
  // budget so the ENG-9122 ceiling (message under 600 chars) still holds with
  // two payloads in it. 150 + 250 + the prefix stays inside it; the single-stream
  // arms keep the original wider budgets, because there is nothing to share with.
  const bothSpoke = firstLine(stderr).length > 0 && unparseableSnippet(r.stdout).length > 0;
  const stderrLine = redactCredentials(firstLine(stderr, bothSpoke ? 150 : 300));
  const stdoutBlob = unparseableSnippet(r.stdout, bothSpoke ? 250 : 400);
  const detail =
    stderrLine && stdoutBlob
      ? `the host probe produced no recognised verdict — this says nothing about the integration. stderr: ${stderrLine} — raw output: ${stdoutBlob}`
      : stderrLine
        ? `the host probe produced no recognised verdict — this says nothing about the integration. stderr: ${stderrLine}`
        : stdoutBlob
          ? `the host probe produced output we could not read as a verdict — this says nothing about the integration. Raw output: ${stdoutBlob}`
          : `host probe failed (exit ${r.responseCode ?? 'n/a'}) with no output`;
  return { verdict: 'probe_error', message: detail, probed_at: null };
}

/**
 * ENG-6443 (#6, Tier C): dead-lettered / quarantined inbound for ONE agent.
 *
 * An inbound message (Slack/Telegram) is "dead-lettered" when it is parked on the
 * host instead of being delivered — moved to a `*-pending-inbound-stale/` dir on a
 * wedge-respawn (the agent couldn't receive it), or moved aside into a
 * `pending-inbound-cleared-<stamp>/` dir by the `clear_pending_inbound` remedial
 * action. Both are recoverable: the marker file still holds the original message.
 * `debug_inspect_dead_letters` LISTS them; `debug_replay_dead_letter` re-injects a
 * selected one back into the agent's live `*-pending-inbound/` dir.
 *
 * SAFETY — marker filenames are ATTACKER-CONTROLLED (written by the agent's own
 * channel MCP from untrusted channel/chat/message ids). They are enumerated host
 * side via `readdir` and are NEVER interpolated into a shell command or used to
 * construct a path from caller input; the projection below carries only routing
 * metadata (which conversation, when, why) — NEVER the message `payload` body, so
 * inspecting a dead letter cannot exfiltrate customer message content into our
 * audit log.
 */
export type DeadLetterChannel = 'slack' | 'telegram';

export interface DeadLetterMarker {
  /**
   * 0-based position in the host's canonical enumeration — display/order only.
   * NOT the replay key: replay matches by (store, channel, marker_name), which is
   * TOCTOU-immune (the index can shift if markers arrive/clear between calls).
   */
  index: number;
  /**
   * Which dead-letter store the marker lives in: `stale` (a wedge-respawn
   * dead-letter) or `cleared:<dirname>` (a `clear_pending_inbound` move-aside).
   */
  store: string;
  channel: DeadLetterChannel;
  /** The on-disk marker filename. Attacker-controlled; the replay key (with store+channel). */
  marker_name: string;
  /** ISO timestamp the channel MCP stamped when it parked the inbound (null if unparseable). */
  received_at: string | null;
  /** True when the marker was flagged undeliverable (agent couldn't receive) before dead-lettering. */
  undeliverable: boolean;
  /** True when the inbound was discretionary/auto-followed (a lower-confidence replay candidate). */
  discretionary: boolean;
  /**
   * Durable-replay attempts already PUSHED for this marker.
   *
   * ENG-10046 — the previous doc on this field said *"null when the marker
   * carries no replay payload"*, and that is FALSE. `replay_count` has no
   * relationship to the payload at all: the marker write omits the field
   * entirely (healthy-path markers stay byte-identical) and the replay path
   * writes `(count ?? 0) + 1` on each re-push. So an absent value means only
   * that no replay was ever pushed.
   *
   * That wrong sentence is how a true zero came to be read as no-signal, and
   * then as evidence about the payload — the tool's own type documentation
   * asserting a relationship that does not exist, in the field at the centre of
   * the question it was reached for. `0` now means "never attempted", which is a
   * fact; `null` is reserved for a value that is PRESENT and not a number.
   */
  replay_count: number | null;
  /**
   * ENG-10046 — does the marker carry a replay payload?
   *
   * A BOOLEAN, never the payload: the body is the customer's message and this
   * projection's allowlist is the structural guarantee it never leaves the host.
   *
   * This is the field that discriminates the two hypotheses every consumer of
   * this tool has been stuck between - `replay_orphaned` (carried a payload, was
   * eligible, nothing came for it) versus `aged_out_unrecoverable` (never
   * carried one, structurally unreplayable) - which require opposite fixes. The
   * tool existed to answer that and could not.
   *
   * TRI-STATE, and the third state is the point. `null` means THIS HOST DID NOT
   * REPORT - an agt-cli predating ENG-10046, or a marker whose JSON would not
   * parse. It does NOT mean "no payload".
   *
   * I shipped this as a plain boolean first and argued for it in the PR: that a
   * tri-state "invites exactly the ambiguity we just removed from replay_count".
   * That was backwards. `replay_count`'s defect was COLLAPSING two different
   * things (never attempted / unreadable) into one value; a third state here
   * SEPARATES them. Rendering "cannot tell" as `false` would make an unknown
   * marker read as structurally unreplayable, sending an operator to the wrong
   * hypothesis with confidence - which is the exact failure this ticket exists
   * to remove, pointed the other way. Raised in review, and correctly.
   */
  has_payload: boolean | null;
  /** Conversation routing id (Slack channel id / Telegram chat id) — NOT message content. */
  conversation: string | null;
  /** Message reference (Slack message_ts / Telegram message_id) — NOT message content. */
  message_ref: string | null;
  /** True when the marker JSON could not be parsed host-side (listed by name only). */
  parse_error: boolean;
}

export interface DeadLettersProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The host the inspect ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** Dead-letter markers in the host's canonical order (slack-stale, telegram-stale, then cleared dirs). */
  markers: DeadLetterMarker[];
  /** Total markers found (== markers.length). */
  total: number;
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  ssm_command_id: string | null;
}

/** Outcome of parsing the host inspect script's JSONL stdout. */
export interface DeadLetterInspectParse {
  markers: DeadLetterMarker[];
  /** Set when the host emitted a `dead_letter_error` sentinel (node missing / bad code_name). */
  hostError: string | null;
}

function coerceDeadLetterChannel(v: unknown): DeadLetterChannel | null {
  return v === 'slack' || v === 'telegram' ? v : null;
}

// ───────────────────── stuck restart requests (ENG-6444) ─────────────────────

/**
 * ENG-6444: how long a `host_agents.restart_requested_at` must sit unacked before
 * it counts as STUCK. Mirrors `STUCK_THRESHOLD_SECONDS` in the API's
 * agent-restart-monitor cron (the writer of the `agent_restart_stuck` alert) — the
 * manager should ack a restart within 1–2 ticks, well inside 15 min. Kept here so
 * the `debug_inspect_restart_requests` read flags `is_stuck` on the same boundary
 * the alert opens on. (The cron keeps its own copy; this is a deliberate mirror,
 * not an import, to avoid the read depending on a cron-internal constant.)
 */
export const STUCK_RESTART_THRESHOLD_SECONDS = 15 * 60;

/**
 * ENG-6444: one pending restart request for the `debug_inspect_restart_requests`
 * read. A "restart request" is a non-null `host_agents.restart_requested_at` the
 * manager hasn't acked yet; `is_stuck` is true once its age crosses
 * `STUCK_RESTART_THRESHOLD_SECONDS` (the point the agent-restart-monitor cron opens
 * an `agent_restart_stuck` alert). The `incident` / `alert` fields surface the
 * open ledger row + paged alert when the cron has already escalated it. Pure
 * routing/metadata — no message content, same projection-not-raw-rows contract as
 * the rest of the surface. `request_clear_restart_request` cancels one.
 */
export interface StuckRestartRequestProjection {
  agent_id: string;
  code_name: string;
  display_name: string | null;
  organization_id: string | null;
  /** The host whose binding carries the unacked restart signal. */
  host: { id: string; name: string | null } | null;
  /** When the restart was requested (the unacked `host_agents.restart_requested_at`). */
  restart_requested_at: string;
  /** Age of the request, in seconds. */
  stuck_seconds: number;
  /** True once `stuck_seconds` crosses the threshold the alert opens on. */
  is_stuck: boolean;
  /** The open `agent_restart_incidents` ledger row, if the cron has escalated it. */
  incident: {
    id: string;
    reason: string | null;
    opened_at: string | null;
    acknowledged_at: string | null;
  } | null;
  /** The open `agent_restart_stuck` alert, if the cron has paged it. */
  alert: {
    id: string;
    severity: string | null;
    message: string | null;
    opened_at: string | null;
  } | null;
}

/**
 * Parse the host inspect script's stdout (one JSON object per line). Pure (no I/O)
 * so it is unit-testable without SSM. Renumbers `index` by array position so the
 * returned order is contiguous and authoritative regardless of host output. Lines
 * that aren't a valid marker object are skipped; a `dead_letter_error` sentinel
 * line surfaces as `hostError`.
 */
export function parseDeadLetterInspectOutput(stdout: string): DeadLetterInspectParse {
  const markers: DeadLetterMarker[] = [];
  let hostError: string | null = null;
  for (const rawLine of (stdout ?? '').split('\n')) {
    const line = rawLine.trim();
    if (!line.startsWith('{') || !line.endsWith('}')) continue;
    let obj: Record<string, unknown>;
    try {
      const v = JSON.parse(line) as unknown;
      if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
      obj = v as Record<string, unknown>;
    } catch {
      continue;
    }
    if (typeof obj['dead_letter_error'] === 'string') {
      hostError = obj['dead_letter_error'];
      continue;
    }
    const channel = coerceDeadLetterChannel(obj['channel']);
    const store = obj['store'];
    const markerName = obj['marker_name'];
    if (!channel || typeof store !== 'string' || typeof markerName !== 'string') continue;
    markers.push({
      index: markers.length,
      store,
      channel,
      marker_name: markerName,
      received_at: typeof obj['received_at'] === 'string' ? obj['received_at'] : null,
      undeliverable: obj['undeliverable'] === true,
      discretionary: obj['discretionary'] === true,
      // ENG-10046: absent ⇒ 0 (never attempted), a fact rather than missing data.
      // `null` only for a value that is present and not a number.
      replay_count:
        obj['replay_count'] === undefined || obj['replay_count'] === null
          ? 0
          : typeof obj['replay_count'] === 'number'
            ? obj['replay_count']
            : null,
      // Tri-state: a boolean survives as itself, anything else (absent on a host
      // that predates this field, or a non-boolean) is "not reported".
      has_payload: typeof obj['has_payload'] === 'boolean' ? (obj['has_payload'] as boolean) : null,
      conversation: typeof obj['conversation'] === 'string' ? obj['conversation'] : null,
      message_ref: typeof obj['message_ref'] === 'string' ? obj['message_ref'] : null,
      parse_error: obj['parse_error'] === true,
    });
  }
  return { markers, hostError };
}

/** Outcome of a replay (re-inject) — parsed from the host replay script's stdout. */
export interface DeadLetterReplayResolution {
  replayed: boolean;
  /** When not replayed: why (`not_found` | `move_failed` | `node_not_found` | `bad_code_name` | `no_output`). */
  reason: string | null;
  channel: string | null;
  store: string | null;
  marker_name: string | null;
  /** The live dir the marker was moved into (e.g. `slack-pending-inbound`). */
  moved_to: string | null;
}

/**
 * Parse the host replay script's stdout. Pure (no I/O). The replay script emits a
 * single JSON object: a success (`replayed:true` + what moved) or a structured
 * reason (`not_found` when the marker is gone, `move_failed`, or a `dead_letter_error`
 * sentinel). Anything unparseable degrades to `no_output`.
 */
export function parseDeadLetterReplayOutput(stdout: string): DeadLetterReplayResolution {
  const fallback: DeadLetterReplayResolution = {
    replayed: false,
    reason: 'no_output',
    channel: null,
    store: null,
    marker_name: null,
    moved_to: null,
  };
  const lines = (stdout ?? '').split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]!.trim();
    if (!line.startsWith('{') || !line.endsWith('}')) continue;
    let obj: Record<string, unknown>;
    try {
      const v = JSON.parse(line) as unknown;
      if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
      obj = v as Record<string, unknown>;
    } catch {
      continue;
    }
    if (typeof obj['dead_letter_error'] === 'string') {
      return { ...fallback, reason: obj['dead_letter_error'] };
    }
    if (obj['replayed'] === true || obj['replayed'] === false) {
      return {
        replayed: obj['replayed'] === true,
        reason: typeof obj['reason'] === 'string' ? obj['reason'] : null,
        channel: typeof obj['channel'] === 'string' ? obj['channel'] : null,
        store: typeof obj['store'] === 'string' ? obj['store'] : null,
        marker_name: typeof obj['marker_name'] === 'string' ? obj['marker_name'] : null,
        moved_to: typeof obj['moved_to'] === 'string' ? obj['moved_to'] : null,
      };
    }
  }
  return fallback;
}

// ENG-9343: the resume-precondition evaluator — "does the condition that
// tripped this breaker still hold?". Re-exported here so `@augmented/core`
// consumers reach it on the same path as the rest of the admin-debug contract.
export * from './resume-precondition.js';
